import * as React from "react"
import * as RPNInput from "react-phone-number-input"
import flags from "react-phone-number-input/flags"
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline"
import Button from "../button/Button"
import Input, { type InputProps } from "./Input"
import { Label } from "./Label"
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../Command"
import { Popover, PopoverTrigger, PopoverContent } from "../Popover"
import { ScrollArea } from "../ScrollArea"
import { cn } from "../../../utils"
import { cva, VariantProps } from "class-variance-authority"

const phoneInputVariants = cva("s", {
  variants: {
    variant: {
      grey: "bg-light-grey",
      white: "bg-white",
    },
  },
})

type PhoneInputProps = Omit<
  React.ComponentProps<"input">,
  "onChange" | "value" | "ref"
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> &
  VariantProps<typeof phoneInputVariants> & {
    onChange?: (value: RPNInput.Value) => void
    inputProps?: Partial<InputProps>
    label?: string
    tooltip?: string
    dutchNumberOnly?: boolean
  }

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<
    React.ComponentRef<typeof RPNInput.default>,
    PhoneInputProps
  >(
    (
      {
        className,
        onChange,
        inputProps,
        defaultCountry = "NL",
        label,
        variant = "white",
        dutchNumberOnly = false,
        ...props
      },
      ref,
    ) => {
      const {
        success,
        error,
        className: inputClassName,
        tooltip,
        ...restInputProps
      } = inputProps || {}

      // If dutchNumberOnly mode, lock country to NL
      const effectiveDefaultCountry = dutchNumberOnly ? "NL" : defaultCountry

      return (
        <div className="flex flex-col gap-2">
          {label && (
            <Label error={error} success={success} tooltip={tooltip}>
              {label}
              {props.required && (
                <span className="text-red-500" aria-hidden="true">
                  {" "}
                  *
                </span>
              )}
            </Label>
          )}

          <RPNInput.default
            ref={ref}
            defaultCountry={effectiveDefaultCountry}
            className={cn("flex", className)}
            flagComponent={FlagComponent}
            countrySelectComponent={(props) => (
              <CountrySelect
                {...props}
                variant={variant}
                disabled={dutchNumberOnly || props.disabled}
              />
            )}
            numberInputProps={{
              ...restInputProps,
              success,
              error,
              variant,
              className: cn(
                phoneInputVariants({ variant }),
                inputClassName,
                "rounded-s-none rounded-e-sm",
                {
                  "ring-primary-green ring-[1px] text-primary-green": success,
                  "ring-red ring-[1px] text-red": error,
                  "opacity-50 cursor-not-allowed":
                    props.disabled || inputProps?.disabled,
                },
              ),
              containerClassName: "w-full block",
            }}
            inputComponent={InputComponent}
            disabled={props.disabled || inputProps?.disabled}
            international={false}
            onChange={(value) => onChange?.(value || ("" as RPNInput.Value))}
            {...props}
          />
        </div>
      )
    },
  )

const InputComponent = ({ className, ...props }: InputProps) => {
  return (
    <Input
      className={cn("rounded-e-sm rounded-s-none", className)}
      containerClassName="block w-full"
      {...props}
    />
  )
}

type CountryEntry = { label: string; value: RPNInput.Country | undefined }

interface CountrySelectProps extends VariantProps<typeof phoneInputVariants> {
  disabled?: boolean
  value: RPNInput.Country
  options: CountryEntry[]
  onChange: (country: RPNInput.Country) => void
  className?: string
}

const CountrySelect = ({
  disabled,
  value: selectedCountry = "NL",
  options: countryList,
  onChange,
  className,
  variant = "white",
}: CountrySelectProps) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="unstyled"
          className={cn(
            phoneInputVariants({ variant }),
            "flex rounded-e-none border-r-0 z-10 focus-visible:ring-[1px] focus-visible:ring-primary-dark-blue focus-visible:border-transparent data-[state=open]:border-transparent data-[state=open]:ring-[1px] data-[state=open]:ring-primary-dark-blue",
            {
              "cursor-not-allowed disabled:bg-white":
                disabled && variant === "white",
            },
            {
              "cursor-not-allowed disabled:bg-light-grey":
                disabled && variant === "grey",
            },
            className,
          )}
          disabled={disabled}
        >
          <div className="pl-2">
            <FlagComponent
              country={selectedCountry}
              countryName={selectedCountry}
            />
          </div>
          {selectedCountry && (
            <span className={cn(disabled ? "pr-2" : "pr-0")}>
              +{RPNInput.getCountryCallingCode(selectedCountry)}
            </span>
          )}
          <ChevronDownIcon
            className={cn(
              "mr-2 size-4 opacity-50",
              disabled ? "hidden" : "opacity-100",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Zoek land" />
          <CommandList>
            <ScrollArea className="h-72">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                    />
                  ) : null,
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country
  onChange: (country: RPNInput.Country) => void
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
}: CountrySelectOptionProps) => {
  return (
    <CommandItem className="gap-2" onSelect={() => onChange(country)}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-foreground/50">{`+${RPNInput.getCountryCallingCode(
        country,
      )}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${
          country === selectedCountry ? "opacity-100" : "opacity-0"
        }`}
      />
    </CommandItem>
  )
}

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country]

  return (
    <span className="flex overflow-hidden">
      {Flag && <Flag title={countryName} />}
    </span>
  )
}

export { PhoneInput }
